// One object holding everything the simulation can change.
//
// Before this (2026-07-30, sim extraction step 2), each of these lived as
// module-level mutable state in its own `game/` module: `let energy` in
// playerStats.ts, `const held = new Set()` in inventory.ts, `const locked = {}`
// in doors.ts, a localStorage-hydrated `const learned` in learnedRunes.ts, and
// `let linkedSourceId` in objectLink.ts. That works for exactly one world in one
// browser tab, and fails for everything Phase 0 is aiming at: a second world in
// an editor, resetting a world without reloading, branching a world to try two
// futures, or replaying a recorded session -- all of which need the state to be
// a value you can hold two of, not a module you can only have one of.
//
// Those modules still exist and still export the same functions; they now read
// and write through here instead of owning the data (see game/src/activeWorld.ts
// for the game's single instance). This file is deliberately free of persistence:
// no localStorage, no save format, no DOM. learnedRunes.ts still persists itself
// to localStorage, but it does so in `game/`, which is why importing the sim
// under Node never touches browser APIs.
import { Facing, RoomDef } from './types';
import { START_FACING, START_ROOM, START_X, START_Y } from './constants';
import { DEFAULT_PHYSICS, PhysicsConfig } from './physics';
import { buildAllRooms, ROOM_BUILDERS } from './rooms';

/** The caster's own reserves -- the quantities a self-sourced cast draws from. */
export interface PlayerState {
  mana: number;
  health: number;
  /** Unlike Mana/Health, Charge starts at 0, not full -- net electric
   * neutrality is the physical rest state (04_Physics_Engine.md's Conservation
   * of Charge), not something the body keeps an ambient reserve of the way it
   * always has some metabolism. The player only ever has Charge because they
   * Transferred it onto themselves from a real charged object first. */
  charge: number;
  /** The caster's own body heat -- what `Transfer`/`Bind`·Energy touch when the
   * caster is a locus, entirely separate from Mana. Starts at a neutral,
   * "Comfortable" midpoint (`physics.player.thermalStart`) rather than full or
   * empty, since both extremes are dangerous (see `checkThermalDanger` in
   * player.ts) -- this is the one player reserve where resting mid-range is
   * the safe state, not either end. */
  thermalState: number;
  /** Where the caster is. Lives here rather than on the scene because casts are
   * position- and facing-relative: Project fires along the facing, Momentum
   * shoves whatever is adjacent and recoils the caster the opposite way. A
   * headless caller has to be able to place the player before casting. */
  roomId: string;
  x: number;
  y: number;
  facing: Facing;
}

/** Which object is currently marked as a Transfer source, if any.
 *
 * Deliberately stores the same bare id/name pair objectLink.ts always did,
 * *not* a room-qualified reference -- a mark made in one room and carried into
 * another still silently falls back to the caster at cast time (see
 * GameScene's applyTransferEffect), and puzzle.spec.ts:318 pins that behavior.
 * Room-qualifying the mark and deriving its validity is a real improvement and
 * a real behavior change, so it belongs in its own commit, not smuggled into a
 * mechanical state move. */
export interface MarkedSource {
  id: string;
  name: string;
}

export interface WorldState {
  /** The numbers this world runs on (see physics.ts). Lives on the world rather
   * than being imported by each rule so that two worlds can be run side by side
   * on different values -- "is this puzzle still solvable if regen is half as
   * fast" is a fork, tune and compare, not a rebuild. Defaults to the shared
   * frozen DEFAULT_PHYSICS; `cloneWorldState` gives a fork its own mutable
   * copy. */
  physics: PhysicsConfig;
  /** Every room, built fresh. Mutated in place as the world is played --
   * melted barriers are removed from `objects`, properties are written
   * directly. That in-place mutation is exactly why an editor must deep-clone
   * on load rather than reuse a builder's output as both the authored
   * definition and the live instance. */
  rooms: Record<string, RoomDef>;
  player: PlayerState;
  /** Item ids currently carried. */
  inventory: Set<string>;
  /** Item ids ever granted, kept even after `inventory` loses them -- a
   * one-use key (see ItemDef.consumedOnUseText) is removed from `inventory`
   * the moment it unlocks its door, and `inventory` alone can't tell "already
   * had this and used it" apart from "never had this at all". A real,
   * reported bug before this existed: a crate's `giveItemId` grant used to
   * gate on `!hasItem(id)`, so once the key it gave was consumed, the same
   * crate read as never-opened again and handed out a fresh one. Append-only,
   * same shape `learnedRunes` already has (a rune is never un-learned
   * either) -- see inventory.ts's `hasEverCollected`. */
  collectedItemIds: Set<string>;
  /** Rune ids learned from in-world books. */
  learnedRunes: Set<string>;
  /** Keyed by lock id rather than by door, since a doorway connecting two rooms
   * is narratively one door: both rooms' DoorDefs share a lock id, so unlocking
   * from either side unlocks both. Absent means locked (see doors.ts). */
  doorLocks: Record<string, boolean>;
  markedSource: MarkedSource | null;
  /** Tile keys ("x,y") in the player's current room that carry flowing water, as
   * of the last `recomputeWaterFlow`. Kept separate from `isWalkable`'s ordinary
   * object/wall check because a flowing tile blocks the *player* without
   * blocking a pushed object from being shoved onto it -- that asymmetry is the
   * entire redirect mechanic (pushing a Boulder into the channel).
   *
   * Derived rather than authored: it is a cache of what `traceWaterFlow` would
   * return for the current room, and every call replaces it wholesale --
   * including the early return for rooms with no water system at all, so a stale
   * room9 entry can never leak into another room's coordinates. It lives here,
   * rather than on the scene as it used to, so the recompute has an owner that
   * exists without a renderer. */
  flowingTiles: Set<string>;
  /** Next id for a runtime-created rock (a Separate·Mass split piece, a merged
   * result). An incrementing counter is enough because nothing ever needs to look
   * one up by a stable, predictable id the way authored objects do -- it only has
   * to never collide, with another runtime rock or with an authored object.
   * Deliberately never rewound while a world is being played: reusing an id a
   * destroyed rock once had would let a stale reference (the marked source, a
   * sprite's objectId) silently rebind to a different rock. */
  nextRockId: number;
  /** Next id for a runtime-created vines object -- a new segment grown into
   * the tile above an existing patch (see `attemptVineGrowth`). Same shape as
   * `nextRockId` and for the same reason (never rewound, only has to never
   * collide); kept as its own counter rather than merged into that one, since
   * the two mechanisms creating runtime objects arrived a session apart and
   * nothing today needs them to share a sequence. */
  nextVineId: number;
  /** Total simulated milliseconds elapsed, and how far into each ambient rule's
   * interval the world currently sits (see tick.ts). Kept here rather than in the
   * caller so that "how long has this world been running" travels with the world
   * -- a saved, branched or replayed world resumes mid-interval exactly where it
   * left off instead of silently restarting both timers. */
  timeMs: number;
  sinceManaRegenMs: number;
  sinceRoomDecayMs: number;
  /** How far into the conveyor cadence the room's belts currently sit (see
   * `sim/conveyor.ts`). Here beside the two ambient rules rather than on each
   * belt, which is where a mob keeps its own: two mobs must not be forced into
   * one phase, and every belt in a room must be -- they are one machine driven
   * off one supply, and a run that stepped a third of a second after the run
   * beside it would be a rhythm to watch instead of a rhythm to count. */
  sinceConveyorMs: number;
}

/** A brand new world at its starting values -- full reserves, no Charge, nothing
 * carried, nothing learned, every door at its authored lock state, and a freshly
 * built set of rooms. This is also exactly what New Game resets to.
 *
 * `physics` defaults to the shared frozen config the game runs on; pass one
 * (from `clonePhysicsConfig`) to run a world on different numbers. */
export function createWorldState(physics: PhysicsConfig = DEFAULT_PHYSICS): WorldState {
  return {
    physics,
    rooms: buildAllRooms(),
    player: {
      mana: physics.player.maxMana,
      health: physics.player.maxHealth,
      charge: 0,
      thermalState: physics.player.thermalStart,
      roomId: START_ROOM,
      x: START_X,
      y: START_Y,
      facing: START_FACING,
    },
    inventory: new Set(),
    collectedItemIds: new Set(),
    learnedRunes: new Set(),
    doorLocks: {},
    markedSource: null,
    flowingTiles: new Set(),
    nextRockId: 1,
    nextVineId: 1,
    timeMs: 0,
    sinceManaRegenMs: 0,
    sinceRoomDecayMs: 0,
    sinceConveyorMs: 0,
  };
}

/** Resets an existing world in place, rather than swapping the object out.
 *
 * In place specifically because the game's modules and the running scene both
 * hold a reference to the same WorldState; replacing it wholesale would leave
 * every one of them pointing at a stale object. New Game needs the thing they
 * all share to change, not to be substituted.
 *
 * Deliberately leaves `physics` alone: the config is what this world is being
 * run on, not a value it accumulated while being played. Resetting a tuned
 * world to try the same experiment again should not silently untune it. */
export function resetWorldState(state: WorldState): void {
  state.rooms = buildAllRooms();
  state.player.mana = state.physics.player.maxMana;
  state.player.health = state.physics.player.maxHealth;
  state.player.charge = 0;
  state.player.thermalState = state.physics.player.thermalStart;
  state.player.roomId = START_ROOM;
  state.player.x = START_X;
  state.player.y = START_Y;
  state.player.facing = START_FACING;
  state.inventory.clear();
  state.collectedItemIds.clear();
  state.learnedRunes.clear();
  for (const key of Object.keys(state.doorLocks)) delete state.doorLocks[key];
  state.markedSource = null;
  state.flowingTiles = new Set();
  state.nextRockId = 1;
  state.nextVineId = 1;
  state.timeMs = 0;
  state.sinceManaRegenMs = 0;
  state.sinceRoomDecayMs = 0;
  state.sinceConveyorMs = 0;
}

/** Everything one player action can change, copied out of a world.
 *
 * **One room, not all of them**, and that is a claim about the simulation rather
 * than a shortcut. Nothing a single action does reaches another room: a cast acts
 * within the current room, `applyRoomDecay` and `solveThermalStep` are handed the
 * room the caster is standing in, mobs only advance there (`advanceMobs`), and
 * `tick.ts` says outright that other rooms do not tick while unvisited. So the
 * rooms the player is not in are identical on both sides of an action, and
 * copying all seventeen to preserve one would be paying seventeen times over on
 * a phone, per turn, forever.
 *
 * The consequence is a real constraint on the caller and it is stated here
 * rather than discovered: a snapshot is only valid for restoring into a world
 * whose player is still in the same room. Crossing a door invalidates every
 * snapshot taken before it. `game/src/undoHistory.ts` enforces that by clearing
 * its stack on a room change, which is also the per-room undo the campaign
 * proposal asks for.
 *
 * Everything else here is world-level and small enough that being precise about
 * it would cost more than copying it. */
export interface WorldSnapshot {
  roomId: string;
  room: RoomDef;
  player: PlayerState;
  inventory: string[];
  collectedItemIds: string[];
  learnedRunes: string[];
  doorLocks: Record<string, boolean>;
  markedSource: MarkedSource | null;
  flowingTiles: string[];
  nextRockId: number;
  nextVineId: number;
  timeMs: number;
  sinceManaRegenMs: number;
  sinceRoomDecayMs: number;
  sinceConveyorMs: number;
}

export function snapshotWorld(state: WorldState): WorldSnapshot {
  return {
    roomId: state.player.roomId,
    room: structuredClone(state.rooms[state.player.roomId]),
    player: { ...state.player },
    inventory: [...state.inventory],
    collectedItemIds: [...state.collectedItemIds],
    learnedRunes: [...state.learnedRunes],
    doorLocks: { ...state.doorLocks },
    markedSource: state.markedSource ? { ...state.markedSource } : null,
    flowingTiles: [...state.flowingTiles],
    nextRockId: state.nextRockId,
    nextVineId: state.nextVineId,
    timeMs: state.timeMs,
    sinceManaRegenMs: state.sinceManaRegenMs,
    sinceRoomDecayMs: state.sinceRoomDecayMs,
    sinceConveyorMs: state.sinceConveyorMs,
  };
}

/** Puts a snapshot back, **in place** -- the same requirement, for the same
 * reason, that `resetWorldState` above spells out: the game's modules and the
 * running scene all hold a reference to one `WorldState` (see
 * `game/src/activeWorld.ts`), so a restore that assigned a new object would
 * leave every one of them reading a world nobody is playing any more. The Sets
 * are emptied and refilled rather than replaced for the same reason.
 *
 * Deliberately leaves `physics` alone. The config is what this world is being
 * *run* on, not something it accumulated by being played -- undoing a move must
 * not silently undo a retune, exactly as resetting one must not.
 *
 * **`nextRockId`/`nextVineId` do get rewound**, which contradicts the note on
 * those fields above ("deliberately never rewound while a world is being
 * played") and is safe only for a specific reason worth writing down. The danger
 * there is a stale reference -- a sprite's `objectId`, the marked source --
 * silently rebinding to a different rock that later reuses the id. A restore
 * closes that off from both ends: `markedSource` comes back from this same
 * snapshot, so it can only name something that existed at the same moment, and
 * the client is required to rebuild its sprites wholesale rather than diffing
 * them (see `game/src/undoHistory.ts`). Rewinding is then not merely safe but
 * necessary: leaving the counter ahead would mean undoing a split and redoing it
 * produced a rock with a different id each time, so a recording of the sequence
 * would not replay. */
export function restoreWorldState(state: WorldState, snapshot: WorldSnapshot): void {
  state.rooms[snapshot.roomId] = structuredClone(snapshot.room);
  Object.assign(state.player, snapshot.player);
  const refill = (set: Set<string>, values: string[]) => {
    set.clear();
    for (const value of values) set.add(value);
  };
  refill(state.inventory, snapshot.inventory);
  refill(state.collectedItemIds, snapshot.collectedItemIds);
  refill(state.learnedRunes, snapshot.learnedRunes);
  for (const key of Object.keys(state.doorLocks)) delete state.doorLocks[key];
  Object.assign(state.doorLocks, snapshot.doorLocks);
  state.markedSource = snapshot.markedSource ? { ...snapshot.markedSource } : null;
  state.flowingTiles = new Set(snapshot.flowingTiles);
  state.nextRockId = snapshot.nextRockId;
  state.nextVineId = snapshot.nextVineId;
  state.timeMs = snapshot.timeMs;
  state.sinceManaRegenMs = snapshot.sinceManaRegenMs;
  state.sinceRoomDecayMs = snapshot.sinceRoomDecayMs;
  state.sinceConveyorMs = snapshot.sinceConveyorMs;
}

/** Rebuilds one room from its authored data and restores the player's own
 * reserves -- Mana and Health to full, thermalState back to its neutral
 * starting point -- what dying (Health reaching 0, see player.ts's
 * `checkDeath`) resets to. Deliberately narrower than resetWorldState/New
 * Game: every other room, the door locks, and everything carried or learned
 * survive untouched -- only the room just failed in, and the body that
 * failed in it, start over. Where the player ends up standing and which way
 * they're facing is the caller's job (`enterRoom` already needs a spawn
 * point for the ordinary door-transition case too, so this doesn't invent a
 * second notion of one). */
export function restartRoom(state: WorldState, roomId: string): void {
  state.rooms[roomId] = ROOM_BUILDERS[roomId]();
  state.player.mana = state.physics.player.maxMana;
  state.player.health = state.physics.player.maxHealth;
  state.player.charge = 0;
  state.player.thermalState = state.physics.player.thermalStart;
}
